// src/pages/events/EventDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Stack
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Event as EventIcon,
  People as PeopleIcon,
  Group as GroupIcon,
  Payment as PaymentIcon,
  Photo as PhotoIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Euro as EuroIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Image as ImageIcon,
  ConfirmationNumber as ConfirmationNumberIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { es } from 'date-fns/locale';
import { doc, getDoc, updateDoc, deleteDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import { uploadToGarage, useGarageUrl } from '../../../garage';
import { format } from 'date-fns';
import EventAttendeesDialog from './EventAttendeesDialog';
import EventVolunteersDialog from './EventVolunteersDialog';
import EventPaymentsDialog from './EventPaymentsDialog';
import EventGalleryDialog from './EventGalleryDialog';

// Componente TabPanel para las pestañas
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`event-tabpanel-${index}`}
      aria-labelledby={`event-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Modal de edición
const EditEventModal = ({ open, onClose, event, onSave }) => {
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    detalles: '',
    fechaInicio: new Date(),
    fechaFin: null,
    ubicacion: '',
    direcciones: '',
    precio: 0,
    organizador: '',
    contacto: '',
    enlace: '',
    categoria: 'general',
    active: true
  });

  const [fileUpload, setFileUpload] = useState({
    file: null,
    uploading: false,
    progress: 0
  });

  const [saving, setSaving] = useState(false);

  // Categorías de eventos
  const categories = [
    { id: 'general', name: 'General' },
    { id: 'cultural', name: 'Cultural' },
    { id: 'deportivo', name: 'Deportivo' },
    { id: 'educativo', name: 'Educativo' },
    { id: 'familiar', name: 'Familiar' },
    { id: 'festivo', name: 'Festivo' }
  ];

  // Inicializar formulario cuando se abre el modal
  useEffect(() => {
    if (open && event) {
      setFormData({
        titulo: event.titulo || '',
        descripcion: event.descripcion || '',
        detalles: event.detalles || '',
        fechaInicio: event.fechaInicio?.seconds 
          ? new Date(event.fechaInicio.seconds * 1000) 
          : new Date(),
        fechaFin: event.fechaFin?.seconds 
          ? new Date(event.fechaFin.seconds * 1000) 
          : null,
        ubicacion: event.ubicacion || '',
        direcciones: event.direcciones || '',
        precio: event.precio || 0,
        organizador: event.organizador || '',
        contacto: event.contacto || '',
        enlace: event.enlace || '',
        categoria: event.categoria || 'general',
        active: event.active !== undefined ? event.active : true
      });
    }
  }, [open, event]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let processedValue = value;
    if (name === 'precio') {
      const numericValue = value.replace(/[^0-9.]/g, '');
      const parts = numericValue.split('.');
      if (parts.length > 2) {
        processedValue = parts[0] + '.' + parts.slice(1).join('');
      } else {
        processedValue = numericValue;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue
    }));
  };

  const handleDateChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFileUpload(prev => ({
        ...prev,
        file: e.target.files[0]
      }));
    }
  };

  const uploadFile = async () => {
    if (!fileUpload.file) return event?.imagen || '';
    setFileUpload(prev => ({ ...prev, uploading: true, progress: 0 }));
    try {
      const url = await uploadToGarage(fileUpload.file, 'events');
      setFileUpload({ file: null, uploading: false, progress: 0 });
      return url;
    } catch (error) {
      setFileUpload(prev => ({ ...prev, uploading: false, progress: 0 }));
      throw error;
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!formData.titulo || !formData.descripcion) {
      return;
    }

    setSaving(true);
    
    try {
      let imageUrl = event?.imagen || '';
      
      if (fileUpload.file) {
        imageUrl = await uploadFile();
      }
      
      const eventData = {
        ...formData,
        fechaInicio: formData.fechaInicio ? Timestamp.fromDate(formData.fechaInicio) : Timestamp.fromDate(new Date()),
        fechaFin: formData.fechaFin ? Timestamp.fromDate(formData.fechaFin) : null,
        precio: Number(formData.precio) || 0,
        imagen: imageUrl,
        updatedAt: serverTimestamp()
      };
      
      await onSave(eventData);
      onClose();
    } catch (error) {
      console.error("Error al guardar:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Editar Evento</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box component="div" sx={{ mt: 1 }}>
          <Grid container spacing={3}>
          <Grid item xs={12} sm={8}>
            <TextField
              required
              label="Título del evento"
              name="titulo"
              value={formData.titulo}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active}
                  onChange={handleInputChange}
                  name="active"
                />
              }
              label="Activo"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              required
              label="Descripción"
              name="descripcion"
              value={formData.descripcion}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={2}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DateTimePicker
                label="Fecha de inicio"
                value={formData.fechaInicio}
                onChange={(newValue) => handleDateChange('fechaInicio', newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined"
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DateTimePicker
                label="Fecha de fin (opcional)"
                value={formData.fechaFin}
                onChange={(newValue) => handleDateChange('fechaFin', newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined"
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Categoría</InputLabel>
              <Select
                name="categoria"
                value={formData.categoria}
                onChange={handleInputChange}
                label="Categoría"
              >
                {categories.map(category => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Precio (€)"
              name="precio"
              type="number"
              value={formData.precio}
              onChange={handleInputChange}
              fullWidth
              InputProps={{ inputProps: { min: 0, step: 0.01 } }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Ubicación"
              name="ubicacion"
              value={formData.ubicacion}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Detalles adicionales"
              name="detalles"
              value={formData.detalles}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={3}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              label="Organizador"
              name="organizador"
              value={formData.organizador}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Contacto"
              name="contacto"
              value={formData.contacto}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Enlace web"
              name="enlace"
              value={formData.enlace}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<ImageIcon />}
              fullWidth
            >
              Cambiar imagen
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
              />
            </Button>
            {fileUpload.uploading && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  Subiendo: {fileUpload.progress}%
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button 
          onClick={handleSave}
          variant="contained" 
          disabled={saving || fileUpload.uploading}
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
        >
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EventDetail = () => {
  const { eventId: id } = useParams();
  const navigate = useNavigate();
  
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  
  // Estados para diálogos
  const [attendeesDialogOpen, setAttendeesDialogOpen] = useState(false);
  const [volunteersDialogOpen, setVolunteersDialogOpen] = useState(false);
  const [paymentsDialogOpen, setPaymentsDialogOpen] = useState(false);
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const eventImageUrl = useGarageUrl(event?.imagen);

  useEffect(() => {
    fetchEvent();
  }, [id]);

  const fetchEvent = async () => {
    if (!id) {
      navigate('/admin/events');
      return;
    }

    setLoading(true);
    try {
      const eventRef = doc(db, 'events', id);
      const eventDoc = await getDoc(eventRef);
      
      if (eventDoc.exists()) {
        setEvent({ id: eventDoc.id, ...eventDoc.data() });
      } else {
        showSnackbar('No se encontró el evento', 'error');
        navigate('/admin/events');
      }
    } catch (error) {
      console.error("Error al cargar evento:", error);
      showSnackbar('Error al cargar el evento', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEvent = async (eventData) => {
    try {
      const eventRef = doc(db, 'events', id);
      await updateDoc(eventRef, eventData);
      
      // Actualizar el estado local
      setEvent(prev => ({ ...prev, ...eventData }));
      showSnackbar('Evento actualizado correctamente', 'success');
    } catch (error) {
      console.error("Error al actualizar evento:", error);
      showSnackbar('Error al actualizar el evento', 'error');
      throw error;
    }
  };

  const handleDeleteEvent = async () => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este evento?')) {
      try {
        await deleteDoc(doc(db, 'events', id));
        showSnackbar('Evento eliminado correctamente', 'success');
        navigate('/admin/events');
      } catch (error) {
        console.error("Error al eliminar evento:", error);
        showSnackbar('Error al eliminar el evento', 'error');
      }
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const formatEventDate = (timestamp) => {
    if (!timestamp) return 'Fecha no especificada';
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    return format(date, "dd 'de' MMMM 'de' yyyy 'a las' HH:mm", { locale: es });
  };

  const isPastEvent = () => {
    if (!event?.fechaInicio) return false;
    const eventDate = event.fechaInicio.seconds 
      ? new Date(event.fechaInicio.seconds * 1000) 
      : new Date(event.fechaInicio);
    return eventDate < new Date();
  };

  const getCategoryColor = (categoria) => {
    const colors = {
      'cultural': '#9C27B0',
      'deportivo': '#4CAF50',
      'educativo': '#2196F3',
      'familiar': '#FF9800',
      'festivo': '#F44336',
      'general': '#757575'
    };
    return colors[categoria] || colors.general;
  };

  const getCategoryName = (categoria) => {
    const names = {
      'cultural': 'Cultural',
      'deportivo': 'Deportivo',
      'educativo': 'Educativo',
      'familiar': 'Familiar',
      'festivo': 'Festivo',
      'general': 'General'
    };
    return names[categoria] || 'General';
  };

  const getTabs = () => {
    const tabs = [
      { label: "Información General", icon: <EventIcon /> },
      { label: `Asistentes (${event?.asistentes?.length || 0})`, icon: <PeopleIcon /> },
      { label: `Voluntarios (${event?.voluntarios?.length || 0})`, icon: <GroupIcon /> }
    ];

    if (event?.precio > 0) {
      tabs.push({ label: "Pagos", icon: <PaymentIcon /> });
    }
    
    if (isPastEvent()) {
      tabs.push({ label: `Galería (${event?.galeria?.length || 0})`, icon: <PhotoIcon /> });
    }

    return tabs;
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!event) {
    return (
      <Container sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="h6" color="text.secondary">
          Evento no encontrado
        </Typography>
      </Container>
    );
  }

  const tabs = getTabs();

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>

      {/* ── Hero banner ── */}
      <Box sx={{
        position: 'relative', borderRadius: 3, overflow: 'hidden', mb: 3,
        height: eventImageUrl ? 260 : 130,
        bgcolor: getCategoryColor(event.categoria)
      }}>
        {eventImageUrl && (
          <Box component="img" src={eventImageUrl} alt={event.titulo}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.72, display: 'block' }} />
        )}
        <Box sx={{
          position: 'absolute', inset: 0, p: { xs: 2, sm: 3 },
          background: eventImageUrl
            ? 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.25) 55%, rgba(0,0,0,0.1) 100%)'
            : 'rgba(0,0,0,0.18)',
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Tooltip title="Volver a eventos">
              <IconButton onClick={() => navigate('/admin/events')}
                sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' } }}>
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Editar evento">
                <IconButton onClick={() => setEditModalOpen(true)}
                  sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' } }}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Eliminar evento">
                <IconButton onClick={handleDeleteEvent}
                  sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', '&:hover': { bgcolor: 'error.main' } }}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
          <Box>
            <Stack direction="row" spacing={1} mb={1} flexWrap="wrap" useFlexGap>
              <Chip label={getCategoryName(event.categoria)} size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.22)', color: '#fff', fontWeight: 600 }} />
              <Chip
                icon={event.active ? <CheckCircleIcon sx={{ fontSize: '14px !important' }} /> : <CancelIcon sx={{ fontSize: '14px !important' }} />}
                label={event.active ? 'Activo' : 'Inactivo'}
                color={event.active ? 'success' : 'default'} size="small" />
              {isPastEvent() && <Chip label="Finalizado" size="small" sx={{ bgcolor: 'rgba(0,0,0,0.45)', color: '#fff' }} />}
              {event.precio > 0 && <Chip label={`${Number(event.precio).toFixed(2)} €`} size="small" color="warning" />}
            </Stack>
            <Typography variant="h4" fontWeight={700}
              sx={{ color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.6)', lineHeight: 1.2 }}>
              {event.titulo}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Stat cards ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { icon: <PeopleIcon />, label: 'Asistentes', value: event.asistentes?.length || 0, color: '#1565c0', onClick: () => setAttendeesDialogOpen(true) },
          { icon: <GroupIcon />, label: 'Voluntarios', value: event.voluntarios?.length || 0, color: '#6a1b9a', onClick: () => setVolunteersDialogOpen(true) },
          ...(event.precio > 0 ? [
            { icon: <ConfirmationNumberIcon />, label: 'Entradas vendidas', value: `${event.sold || 0}/${event.totalpass || '∞'}`, color: '#0277bd', onClick: () => setPaymentsDialogOpen(true) },
            { icon: <EuroIcon />, label: 'Recaudado', value: `${((event.sold || 0) * (event.precio || 0)).toFixed(2)} €`, color: '#2e7d32' }
          ] : []),
          ...(isPastEvent() ? [
            { icon: <PhotoIcon />, label: 'Fotos galería', value: event.galeria?.length || 0, color: '#e65100', onClick: () => setGalleryDialogOpen(true) }
          ] : [])
        ].map((s, i) => (
          <Grid item xs={6} sm={4} md key={i}>
            <Card onClick={s.onClick} sx={{
              height: '100%', cursor: s.onClick ? 'pointer' : 'default',
              transition: 'all .2s',
              ...(s.onClick && { '&:hover': { transform: 'translateY(-3px)', boxShadow: 4 } })
            }}>
              <CardContent sx={{ textAlign: 'center', pb: '12px !important' }}>
                <Avatar sx={{ bgcolor: s.color + '18', color: s.color, mx: 'auto', mb: 1, width: 44, height: 44 }}>
                  {s.icon}
                </Avatar>
                <Typography variant="h5" fontWeight={700}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── Tabs ── */}
      <Paper sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
            {tabs.map((tab, i) => (
              <Tab key={i} label={tab.label} icon={tab.icon} iconPosition="start" />
            ))}
          </Tabs>
        </Box>

        {/* Tab 0: Información */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={event.imagen ? 7 : 12}>
              <Typography variant="h6" fontWeight={600} gutterBottom>Descripción</Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>{event.descripcion}</Typography>

              <Grid container spacing={2}>
                {[
                  { icon: <CalendarIcon color="primary" />, label: 'Inicio', value: formatEventDate(event.fechaInicio), bg: 'primary.50' },
                  ...(event.fechaFin ? [{ icon: <CalendarIcon color="secondary" />, label: 'Fin', value: formatEventDate(event.fechaFin), bg: 'secondary.50' }] : []),
                  ...(event.ubicacion ? [{ icon: <LocationIcon color="error" />, label: 'Ubicación', value: event.ubicacion, bg: 'error.50' }] : []),
                  ...(event.precio > 0 ? [{ icon: <EuroIcon color="warning" />, label: 'Precio entrada', value: `${Number(event.precio).toFixed(2)} €`, bg: 'warning.50' }] : [])
                ].map((item, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ bgcolor: item.bg, width: 38, height: 38 }}>{item.icon}</Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">{item.label}</Typography>
                        <Typography variant="body2" fontWeight={500}>{item.value}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {event.detalles && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                    DETALLES ADICIONALES
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
                    {event.detalles}
                  </Typography>
                </Box>
              )}
            </Grid>

            {event.imagen && (
              <Grid item xs={12} md={5}>
                <Box component="img" src={eventImageUrl} alt={event.titulo}
                  sx={{ width: '100%', borderRadius: 2, objectFit: 'cover', maxHeight: 340 }} />
              </Grid>
            )}
          </Grid>
        </TabPanel>

        {/* Tab 1: Asistentes */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Avatar sx={{ width: 72, height: 72, bgcolor: '#1565c018', mx: 'auto', mb: 2 }}>
              <PeopleIcon sx={{ fontSize: 40, color: '#1565c0' }} />
            </Avatar>
            <Typography variant="h3" fontWeight={700}>{event.asistentes?.length || 0}</Typography>
            <Typography color="text.secondary" mb={3}>
              {(event.asistentes?.length || 0) === 1 ? 'persona inscrita' : 'personas inscritas'}
            </Typography>
            <Button variant="contained" startIcon={<PeopleIcon />} onClick={() => setAttendeesDialogOpen(true)}>
              Ver lista completa
            </Button>
          </Box>
        </TabPanel>

        {/* Tab 2: Voluntarios */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Avatar sx={{ width: 72, height: 72, bgcolor: '#6a1b9a18', mx: 'auto', mb: 2 }}>
              <GroupIcon sx={{ fontSize: 40, color: '#6a1b9a' }} />
            </Avatar>
            <Typography variant="h3" fontWeight={700}>{event.voluntarios?.length || 0}</Typography>
            <Typography color="text.secondary" mb={3}>
              {(event.voluntarios?.length || 0) === 1 ? 'voluntario' : 'voluntarios'}
            </Typography>
            <Button variant="contained" color="secondary" startIcon={<GroupIcon />} onClick={() => setVolunteersDialogOpen(true)}>
              Ver lista completa
            </Button>
          </Box>
        </TabPanel>

        {/* Tab Pagos */}
        {event.precio > 0 && (() => {
          const idx = 3;
          return (
            <TabPanel value={tabValue} index={idx}>
              <Grid container spacing={2} justifyContent="center" sx={{ py: 3 }}>
                <Grid item xs={6} sm={4}>
                  <Card sx={{ textAlign: 'center' }}>
                    <CardContent>
                      <Typography variant="h3" fontWeight={700} color="primary">{event.sold || 0}</Typography>
                      <Typography variant="caption" color="text.secondary">de {event.totalpass || '∞'} entradas vendidas</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Card sx={{ textAlign: 'center' }}>
                    <CardContent>
                      <Typography variant="h3" fontWeight={700} color="success.main">
                        {((event.sold || 0) * event.precio).toFixed(2)} €
                      </Typography>
                      <Typography variant="caption" color="text.secondary">recaudado total</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sx={{ textAlign: 'center' }}>
                  <Button variant="contained" startIcon={<PaymentIcon />} onClick={() => setPaymentsDialogOpen(true)}>
                    Ver detalle de pagos
                  </Button>
                </Grid>
              </Grid>
            </TabPanel>
          );
        })()}

        {/* Tab Galería */}
        {isPastEvent() && (() => {
          const idx = 3 + (event.precio > 0 ? 1 : 0);
          return (
            <TabPanel value={tabValue} index={idx}>
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Avatar sx={{ width: 72, height: 72, bgcolor: '#e6510018', mx: 'auto', mb: 2 }}>
                  <PhotoIcon sx={{ fontSize: 40, color: '#e65100' }} />
                </Avatar>
                <Typography variant="h3" fontWeight={700}>{event.galeria?.length || 0}</Typography>
                <Typography color="text.secondary" mb={3}>
                  {(event.galeria?.length || 0) === 1 ? 'foto' : 'fotos'} en la galería
                </Typography>
                <Button variant="contained" color="warning" startIcon={<PhotoIcon />} onClick={() => setGalleryDialogOpen(true)}>
                  Gestionar galería
                </Button>
              </Box>
            </TabPanel>
          );
        })()}
      </Paper>

      {/* Modal de edición */}
      <EditEventModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        event={event}
        onSave={handleSaveEvent}
      />

      {/* Diálogos */}
      <EventAttendeesDialog
        open={attendeesDialogOpen}
        onClose={() => setAttendeesDialogOpen(false)}
        eventId={event.id}
        eventTitle={event.titulo}
      />
      
      <EventVolunteersDialog
        open={volunteersDialogOpen}
        onClose={() => setVolunteersDialogOpen(false)}
        eventId={event.id}
        eventTitle={event.titulo}
      />

      {event.precio > 0 && (
        <EventPaymentsDialog
          open={paymentsDialogOpen}
          onClose={() => setPaymentsDialogOpen(false)}
          eventId={event.id}
          eventTitle={event.titulo}
          eventPrice={event.precio}
        />
      )}

      {isPastEvent() && (
        <EventGalleryDialog
          open={galleryDialogOpen}
          onClose={() => setGalleryDialogOpen(false)}
          eventId={event.id}
          eventTitle={event.titulo}
          currentGallery={event.galeria || []}
        />
      )}

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EventDetail;